import { Chart } from 'components/Chart/Chart'
import { createPath } from 'history'
import PropTypes from 'prop-types'
import qs from 'qs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { COLORS } from 'modules/Statistics/colors'
import StatisticsLayout from 'modules/Statistics/Layout'
import Breadcrumbs from 'components/Breadcrumbs'
import { ChartLegend } from 'modules/Statistics/Layout/ChartLegend'
import TableComponent from 'modules/Statistics/Layout/Table'
import NETWORK_PREFIX_MAP from 'modules/Statistics/networkPrefixMap'
import styles from 'modules/Statistics/statistics.module.scss'
import useColumnConfig from 'modules/Statistics/Account/columnsConfig'
import useChartMeasures from 'modules/Statistics/Account/useChartMeasures'
import { getColumnsIDs, getHiddenColumns } from 'modules/Statistics/utils'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import FilterPanel from 'components/Filters/FilterPanel'
import config from 'modules/Statistics/Account/filters'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'

const StatisticsAccount = ({ drawerState = false }) => {
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation(['statistics'])
  const dispatch = useDispatch()
  const [statisticsAccountTableConfig, setStatisticsAccountTableConfig] =
    useLocalStorage('statisticsAccountTableConfig', { hiddenColumns: {} })
  const { network, externalId } = useParams()

  const { chart, table, login } = useSelector((state) => ({
    chart: state['statistics/account'].chart,
    table: state['statistics/account'].table,
    login: state['statistics/account'].login,
  }))

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['statistics/account'].filters),
    config: config(network),
    additionalFilterState: useSelector(
      (state) => state['statistics/filters'].filters,
    ),
    setAction: 'STATISTICS/ACCOUNT_SET_FILTER',
    nonClearableFilters: ['date_group_period', 'include_vat'],
  })

  const [filtersState, setAccountFiltersState] = useState(
    omit(filterManager.filters, ['include_vat']),
  )

  useEffect(() => {
    const omittedFilters = omit(filterManager.filters, ['include_vat'])
    !isEqual(filtersState, omittedFilters) &&
      setAccountFiltersState(omittedFilters)
  }, [filterManager.filters])

  const [tablePagination, setTablePagination] = useLocalStorage(
    'statisticsTablePagination',
    { limit: 25 },
  )

  const [limit, setLimit] = useState(tablePagination.limit)

  const columns = useColumnConfig(
    filterManager.filters?.include_vat,
    table.data,
    table.summary,
    network,
    externalId,
  )

  const chartConfig = useChartMeasures(network)

  useEffect(() => {
    dispatch({
      type: 'STATISTICS/ACCOUNT_SETUP_CHART_CONFIG',
      payload: chartConfig,
    })
  }, [chartConfig])

  const columnsShown = useMemo(() => {
    let columnsShown = []
    const { hiddenColumns } = statisticsAccountTableConfig

    !!columns?.length &&
      columns.forEach((column) => {
        if ('columns' in column) {
          columnsShown = [
            ...columnsShown,
            {
              ...column,
              columns: column.columns.filter(
                ({ id, hideable = true }) =>
                  !hideable || !(id in hiddenColumns),
              ),
            },
          ]
        } else {
          const { id, hideable = true } = column
          if (!hideable || !(id in hiddenColumns)) {
            columnsShown = [...columnsShown, column]
          }
        }
      })

    return columnsShown
  }, [statisticsAccountTableConfig, columns])

  useEffect(() => setTablePagination({ limit: limit }), [limit])

  useEffect(() => {
    dispatch({
      type: 'STATISTICS/ACCOUNT_FETCH_CHART',
      payload: {
        externalId: externalId,
        network: NETWORK_PREFIX_MAP[network],
      },
    })
  }, [filtersState])

  useEffect(() => {
    if (externalId) {
      const net = NETWORK_PREFIX_MAP[network]

      if (net !== 'yd') {
        return
      }
      dispatch({
        type: 'STATISTICS/GET_ACCOUNT_LOGIN',
        payload: { externalId, network: net },
      })
    }
  }, [externalId])

  const changeChartType = useCallback(
    () =>
      dispatch({
        type: 'STATISTICS/SET_CHART_TYPE',
        payload: { type: chart.chartType === 'Line' ? 'Bar' : 'Line' },
      }),
    [chart],
  )

  return (
    <StatisticsLayout
      drawerState={drawerState}
      BreadcrumbsComponent={
        <Breadcrumbs
          links={[
            {
              title: t('breadcrumbs.back_to_accounts_list'),
              to: createPath({
                pathname: pathGen(ROUTES.STATISTICS),
                search: qs.stringify(filterManager.params, {
                  arrayFormat: 'brackets',
                }),
              }),
            },
            {
              title: t('breadcrumbs.campaigns_for_account', {
                account_title: login || externalId,
              }),
              to: pathGen(ROUTES.STATISTICS_ACCOUNT, {
                network,
                externalId,
              }),
            },
          ]}
        />
      }
      FilterPanel={
        filterManager.filters && (
          <FilterPanel
            filterManager={filterManager}
            className={styles['filter-panel']}
          />
        )
      }
      ChartComponent={
        <Chart
          colors={COLORS}
          className={styles.chart}
          chartHeaderClassName={styles['chart-header']}
          chartType={chart.chartType}
          items={chart.data}
          metrics={chart.metrics}
          loading={chart.loading}
          date_group_period={{
            value: filterManager.filters?.date_group_period ?? 'day',
            options: filterManager.configuration.date_group_period.options,
          }}
          include_vat={filterManager.filters?.include_vat}
          summary={chart.summary}
          measure={chart.measure}
          changeChartPeriod={(value) =>
            filterManager.updateFilter({
              fieldName: 'date_group_period',
              value: value,
            })
          }
          changeChartType={changeChartType}
          chartLegend={
            <ChartLegend
              include_vat={filterManager.filters?.include_vat}
              measure={chart.measure}
              metrics={chart.metrics}
              summary={chart.summary}
              handleClick={(id) => {
                dispatch({
                  type: 'STATISTICS/TOGGLE_METRIC_HIDDEN',
                  payload: { id },
                })
              }}
            />
          }
        />
      }
      TableComponent={
        <TableComponent
          {...table}
          filters={filterManager.filters || { include_vat: true }}
          columns={columnsShown}
          limit={limit}
          tableSwitchers={[
            {
              name: 'pagination',
              value: {
                value: limit,
                label: `${t('pagination.limit_button_label')} ${limit}`,
              },
              values: [
                { value: 25, label: t('pagination.limit_by', { pages: 25 }) },
                { value: 50, label: t('pagination.limit_by', { pages: 50 }) },
                { value: 100, label: t('pagination.limit_by', { pages: 100 }) },
              ],
              onChange: setLimit,
            },
            {
              name: 'columns',
              type: 'multiple',
              placeholder: t('table_switcher.choose_columns_placeholder'),
              listTitle: t('table_switcher.choose_columns'),
              value: getColumnsIDs(columnsShown),
              values: columns
                .filter(({ hideable = true }) => hideable)
                .reduce((a, { Header = '', id, columns = null }) => {
                  if (columns) {
                    return [
                      ...a,
                      ...columns
                        .filter(({ hideable = true }) => hideable)
                        .map(({ Header = '', id }) => ({
                          value: id,
                          label: Header,
                        })),
                    ]
                  } else {
                    return [
                      ...a,
                      {
                        value: id,
                        label: Header,
                      },
                    ]
                  }
                }, []),
              onChange: (selectionArr) =>
                setStatisticsAccountTableConfig(
                  getHiddenColumns(selectionArr, columns),
                ),
              onReset: () =>
                setStatisticsAccountTableConfig(
                  getHiddenColumns(getColumnsIDs(columns), columns),
                ),
            },
          ]}
          initialState={{
            sortBy: [
              {
                id: 'campaign.name',
                asc: true,
              },
            ],
          }}
          dropTableData={() =>
            dispatch({
              type: 'STATISTICS/ACCOUNT_DROP_TABLE_DATA',
            })
          }
          onTableParamsUpdate={(params) =>
            dispatch({
              type: 'STATISTICS/ACCOUNT_FETCH_TABLE',
              payload: {
                ...params,
                externalId: externalId,
                network: NETWORK_PREFIX_MAP[network],
              },
            })
          }
        />
      }
    />
  )
}

StatisticsAccount.propTypes = {
  drawerState: PropTypes.bool,
}

export default StatisticsAccount
